import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { formatImageUrl } from '../utils/imageUtils';
import { adminAPI } from '../utils/api';

const EyeIcon = () => (
  <svg className="w-3.5 h-3.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const HeartSmIcon = () => (
  <svg className="w-3.5 h-3.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const PlayIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const formatCount = (n) => {
  if (!n && n !== 0) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
};

const ReelsProfilePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const creatorId = searchParams.get('creatorId');
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creator, setCreator] = useState(null);

  useEffect(() => {
    if (!creatorId) {
      navigate('/reels', { replace: true });
      return;
    }
    fetchCreatorReels();
  }, [creatorId]);

  const fetchCreatorReels = async () => {
    try {
      const res = await adminAPI.getCreatorReels(creatorId);
      if (res.data?.success) {
        setReels(res.data.data || []);
        if (res.data.data?.length > 0) {
          setCreator(res.data.data[0].creator_id);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const totalViews = reels.reduce((s, r) => s + (r.view_count || 0), 0);
  const totalLikes = reels.reduce((s, r) => s + (r.like_count || 0), 0);

  if (loading) return (
    <div className="bg-dark h-screen w-full fixed inset-0 overflow-hidden flex flex-col">
      <div className="shrink-0"><AppHeader /></div>
      <div className="flex-1 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
      <div className="shrink-0"><BottomNav /></div>
    </div>
  );

  return (
    <div className="bg-dark h-screen w-full fixed inset-0 overflow-hidden flex flex-col">
      <div className="shrink-0">
        <div className="bg-gray-900 px-4 py-3 flex items-center gap-3 border-b border-gray-800">
          <button onClick={() => navigate('/reels')} className="text-white p-1">
            <ArrowLeftIcon />
          </button>
          <h1 className="text-lg font-bold text-light">Creator Profile</h1>
        </div>
      </div>

      <main className="flex-1 overflow-y-auto px-4 pt-4 pb-4">
        {/* Profile header */}
        <div className="bg-gray-900 rounded-xl p-5 border border-gray-800 mb-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-gold flex items-center justify-center text-dark font-bold text-xl overflow-hidden ring-2 ring-gold/50 shrink-0">
              {creator?.profile_image ? (
                <img src={formatImageUrl(creator.profile_image)} alt="" className="w-full h-full object-cover" />
              ) : (
                creator?.name?.charAt(0)?.toUpperCase() || 'U'
              )}
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-light truncate">{creator?.name || 'Creator'}</h1>
              <p className="text-sm text-gray-400 capitalize">{creator?.role || 'Creator'}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center mb-4">
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-xl font-bold text-gold">{formatCount(reels.length)}</p>
              <p className="text-xs text-gray-400">Reels</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-xl font-bold text-gold">{formatCount(totalViews)}</p>
              <p className="text-xs text-gray-400">Views</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-xl font-bold text-gold">{formatCount(totalLikes)}</p>
              <p className="text-xs text-gray-400">Likes</p>
            </div>
          </div>

          <button onClick={() => navigate(`/reels?creatorId=${creatorId}`)}
            className="w-full bg-gold text-dark py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2">
            <PlayIcon />
            View All Reels
          </button>
        </div>

        {/* Reels grid */}
        <h2 className="text-lg font-semibold text-light mb-3">Uploads ({reels.length})</h2>
        {reels.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            <p>No reels uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {reels.map(reel => (
              <div
                key={reel._id}
                onClick={() => navigate(`/reels?creatorId=${creatorId}`)}
                className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden cursor-pointer active:scale-95 transition-transform"
              >
                <div className="aspect-[9/16] bg-black relative">
                  {reel.media_url?.endsWith('.mp4') ? (
                    <video src={formatImageUrl(reel.media_url)} className="w-full h-full object-cover" preload="metadata" />
                  ) : (
                    <img src={formatImageUrl(reel.media_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  )}
                  <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1 rounded">
                    {reel.media_url?.endsWith('.mp4') ? '🎬' : '📷'}
                  </div>
                </div>
                <div className="p-2">
                  <p className="text-xs text-gray-300 line-clamp-1">{reel.caption || 'No caption'}</p>
                  <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-1">
                    <span><EyeIcon /> {formatCount(reel.view_count)}</span>
                    <span><HeartSmIcon /> {formatCount(reel.like_count)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <div className="shrink-0"><BottomNav /></div>
    </div>
  );
};

export default ReelsProfilePage;